'use client';

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { STAFF_ROLE, DOCTOR_ROLE } from '@/auth/roles';
import { isNavConfined } from '@/app/lib/nav-confinement';
import { useNavGuard } from '@/shared/store/nav-guard';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

/**
 * Bottom-of-SideNav account widget. Avatar trigger pops out a profile menu
 * with the entries that Flutter PM exposes from the same corner: Organization
 * Settings, Account Settings, Logout.
 *
 * Role-based visibility (PROD-863, Flutter `menu_container_screen.dart:626-641`):
 *  - **Organization Settings** — visible if user has ANY of 5
 *    Manage_* view permissions: Manage_Organization_Details,
 *    Manage_Practice_Details, Manage_Doctors, Manage_Staff,
 *    Manage_Roles_Permission.
 *  - **Account Settings** — visible if user.role === "2" (staff)
 *    or "3" (doctor). Numeric role constants from Flutter
 *    `scrips_core/lib/constants/app_constants.dart:721-724`
 *    (orgAdmin=1, staff=2, doctor=3). Entry remains disabled
 *    pending the Account Settings React surface port.
 *
 * Role confinement (PROD-2396): for a confined role BOTH settings rows are
 * dropped and only Logout remains. This is the Flutter precedent, not an
 * invention — `menu_widget.dart:123` removes the Profile row from this same
 * popup for its confined role. It matters here because a Technician is in
 * the STAFF bucket, so Account Settings would otherwise show for someone
 * whose route guard (`confinedRedirectPath`) sends every non-`/` path back to
 * the calendar: a visible row that bounces. Removing the row and closing the
 * route is one decision, made in `@/app/lib/nav-confinement.ts`. Logout is
 * untouched — it ends the session rather than navigating anywhere.
 *
 * Chrome only. The settings APIs enforce no role (PROD-2388).
 */

/** `expanded`: the rail is open, so the name and role sit beside the avatar (the practitioner app's profile row). */
export function ProfilePopover({ expanded = false }: { expanded?: boolean } = {}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  // Route profile-menu navigations through the shell unsaved-changes guard so
  // leaving a dirty patient Add/Edit via Settings / Logout prompts to discard
  // instead of silently dropping the edits. Off the edit page this is a no-op
  // passthrough. (Forced logouts — SignalR force-logout, session-expired — do
  // NOT go through here, so they still bypass the guard as intended.)
  const runGuarded = useNavGuard((s) => s.run);

  // Org Settings — Flutter ORs 5 single-key validatePermission() calls
  // (menu_container_screen.dart:626-638). usePermission ANDs across keys,
  // so we run 5 separate calls and OR the booleans inline.
  const canManageOrganizationDetails = usePermission(
    ['Manage_Organization_Details'],
    { view: true },
  );
  const canManagePracticeDetails = usePermission(['Manage_Practice_Details'], {
    view: true,
  });
  const canManageDoctors = usePermission(['Manage_Doctors'], { view: true });
  const canManageStaff = usePermission(['Manage_Staff'], { view: true });
  const canManageRolesPermission = usePermission(['Manage_Roles_Permission'], {
    view: true,
  });
  // Confined roles (PROD-2396) get Logout and nothing else.
  const isConfined = isNavConfined(user);
  const isOrgSettingsVisible =
    !isConfined &&
    (canManageOrganizationDetails ||
    canManagePracticeDetails ||
    canManageDoctors ||
    canManageStaff ||
    canManageRolesPermission);

  // Account Settings — Flutter role check (menu_container_screen.dart:639-641)
  const isAccSettingsVisible =
    !isConfined && (user?.role === STAFF_ROLE || user?.role === DOCTOR_ROLE);

  const fullName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || 'User';
  const initials = `${user?.firstName?.[0] ?? 'U'}${user?.lastName?.[0] ?? ''}`.toUpperCase();
  // The role in words (Flutter app_constants: orgAdmin=1, staff=2, doctor=3).
  const roleLabel = user?.role === '1' ? 'Organisation admin' : user?.role === DOCTOR_ROLE ? 'Doctor' : user?.role === STAFF_ROLE ? 'Staff' : 'Signed in';
  const email = user?.email ?? '';

  const go = (path: string) => () => {
    setOpen(false);
    runGuarded(() => navigate(path));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <button
            type="button"
            aria-label="Open account menu"
            className="flex min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-full text-left focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent-primary)]"
          />
        }
      >
        <Avatar size="default">
          <AvatarFallback className="bg-[var(--accent-primary)] text-[var(--color-text-inverse)] text-xs font-semibold">
            {initials}
          </AvatarFallback>
        </Avatar>
        {/* The profile row, identical in both apps (12 Oct): name 13/500 over the role 12/400 secondary. */}
        {expanded && (
          <span data-rail-profile className="min-w-0">
            <span className="block truncate text-sm font-medium text-[var(--ink-body)]">{fullName}</span>
            <span className="block truncate text-xs text-[var(--ink-secondary)]">{roleLabel}</span>
          </span>
        )}
      </PopoverTrigger>
      {/* The profile menu, identical in both apps (Antonin, 12 Oct): to the right of the rail,
          bottom-aligned with the avatar row, 256px; the name (13/500) over a sub-line (12/400
          secondary), a divider; items 13/400 with a grey hover, sentence case, a chevron for a
          sub-page; Log out last after a divider. No zoom animation. */}
      <PopoverContent
        side="right"
        align="end"
        alignOffset={-2}
        sideOffset={8}
        className="w-64 rounded-[var(--radius-rail-item)] border border-[var(--surface-line)] bg-[var(--surface-read)] p-2 shadow-overlay !animate-none !transition-none"
      >
        <div data-user-profile className="px-2 pt-1 pb-2">
          <div className="truncate text-sm font-medium text-[var(--ink-body)]">{fullName}</div>
          <div className="truncate text-xs text-[var(--ink-secondary)]">{roleLabel}{email ? ` · ${email}` : ''}</div>
        </div>
        <hr className="m-0 border-0 border-t border-[var(--surface-line)]" />
        <ul className="m-0 list-none p-0 py-1">
          {isOrgSettingsVisible && (
            <MenuItem onClick={go('/settings')}>
              <span>Organisation settings</span>
              <ChevronRight className="size-4 text-[var(--ink-meta)]" />
            </MenuItem>
          )}
          {/* Account Settings — Flutter routes to the logged-in user's own
              Doctor/Staff edit page via SetMenuSelection(selectedIndex: -1)
              → AccSettingsStackWidget. React mirror: /settings/account
              resolves by role into the edit page in account mode. The role
              gate (PROD-863) applies: only staff/doctor see it. */}
          {isAccSettingsVisible && (
            <MenuItem onClick={go('/settings/account')}>
              <span>Account settings</span>
              <ChevronRight className="size-4 text-[var(--ink-meta)]" />
            </MenuItem>
          )}
        </ul>
        <hr className="my-1 border-0 border-t border-[var(--surface-line)]" />
        <ul className="m-0 list-none p-0">
          <MenuItem
            onClick={() => {
              setOpen(false);
              runGuarded(() => {
                void logout();
              });
            }}
          >
            <span>Log out</span>
          </MenuItem>
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function MenuItem({
  children,
  onClick,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={cn(
          'flex w-full items-center justify-between gap-3 rounded-input px-2 py-2 text-left text-sm text-[var(--ink-body)] transition-colors',
          disabled
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:bg-[var(--surface-panel)] cursor-pointer',
        )}
      >
        {children}
      </button>
    </li>
  );
}
